import API from "./api";

/**
 * Pricing Service
 * Handles pricing rules and price calculations only
 * For admin offer operations, use adminOfferService instead
 */
const pricingService = {
  // ===== PRICING RULES =====

  /**
   * Create a new pricing rule
   */
  createPricingRule: (ruleData) => API.post("/pricing/rules", ruleData),

  /**
   * Get all pricing rules with filters
   */
  getPricingRules: (params = {}) => API.get("/pricing/rules", { params }),

  /**
   * Get pricing rule by ID
   */
  getPricingRuleById: (id) => API.get(`/pricing/rules/${id}`),

  /**
   * Update pricing rule
   */
  updatePricingRule: (id, ruleData) =>
    API.put(`/pricing/rules/${id}`, ruleData), // ✅ Backend uses PUT not PATCH

  /**
   * Delete pricing rule
   */
  deletePricingRule: (id) => API.delete(`/pricing/rules/${id}`),

  /**
   * Clone pricing rule
   */
  clonePricingRule: (id, name) =>
    API.post(`/pricing/rules/${id}/clone`, { name }),

  /**
   * Toggle pricing rule status
   */
  togglePricingRuleStatus: (id) => API.patch(`/pricing/rules/${id}/toggle`),

  // ===== PRICE CALCULATIONS =====

  /**
   * Calculate pricing for a single product
   */
  calculatePricing: (pricingData) => {
    // Client-side validation
    if (!pricingData.branchCode) {
      return Promise.reject(
        new Error("Branch code is required for pricing calculation")
      );
    }
    if (!pricingData.product) {
      return Promise.reject(new Error("Product information is required"));
    }

    return API.post("/pricing/calculate", pricingData);
  },

  /**
   * Calculate bulk pricing for multiple products
   */
  calculateBulkPricing: (bulkData) => {
    // Client-side validation
    if (!bulkData.branchCode) {
      return Promise.reject(
        new Error("Branch code is required for pricing calculation")
      );
    }
    if (!bulkData.products || !Array.isArray(bulkData.products)) {
      return Promise.reject(new Error("Products array is required"));
    }
    if (bulkData.products.length === 0) {
      return Promise.reject(new Error("Products array cannot be empty"));
    }
    if (bulkData.products.length > 100) {
      return Promise.reject(
        new Error("Cannot calculate pricing for more than 100 products at once")
      );
    }

    return API.post("/pricing/calculate-bulk", bulkData);
  },

  // ===== HELPER METHODS =====

  /**
   * Calculate price with applied offers
   * Note: This is a convenience method that combines pricing calculation
   * Actual offer validation should be done through the offers API
   */
  calculatePriceWithOffers: async (
    product,
    branchCode,
    offerCodes = [],
    options = {}
  ) => {
    try {
      const pricingData = {
        product,
        branchCode,
        storeCode: options.storeCode,
        customerType: options.customerType || "RETAIL",
        quantity: options.quantity || 1,
        offerCodes, // Pass offer codes for backend to validate and apply
      };

      const response = await API.post("/pricing/calculate", pricingData);
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export default pricingService;
